import { Box } from "@twilio-paste/core/box";
import { Flex } from "@twilio-paste/core/flex";
import { Media, Message } from "@twilio/conversations";
import { Key, KeyboardEvent, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { AppState } from "../store/definitions";
import { parseMessageBody } from "../utils/parseMessageBody";
import { FilePreview } from "./FilePreview";
import {
  bodyStyles,
  bubbleAndAvatarContainerStyles,
  getInnerContainerStyles,
  outerContainerStyles,
} from "./styles/MessageBubble.styles";

const doubleDigit = (number: number) => `${number < 10 ? 0 : ""}${number}`;

export const MessageBubble = ({
  message,
  isLast,
  isLastOfUserGroup,
  focusable,
  updateFocus,
}: {
  message: Message;
  isLast: boolean;
  isLastOfUserGroup: boolean;
  focusable: boolean;
  updateFocus: (newFocus: number) => void;
}) => {
  const [read, setRead] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const {
    conversationsClient,
    conversation,
    participants,
    users,
    fileAttachmentConfig,
  } = useSelector((state: AppState) => ({
    conversationsClient: state.chat.conversationsClient,
    conversation: state.chat.conversation,
    participants: state.chat.participants,
    users: state.chat.users,
    fileAttachmentConfig: state.config.fileAttachment,
  }));
  const messageRef = useRef<HTMLDivElement>(null);

  const callChiediInformazioni = async () => {
    try {
      if (!conversation) {
        console.error("Conversation is undefined");
        return;
      }
      let preparedMessage = conversation.prepareMessage();
      preparedMessage = preparedMessage.setBody("Chiedi informazioni");
      await preparedMessage.build().send();
    } catch (error) {
      console.error("Error sending message", error);
    }
  };

  const belongsToCurrentUser =
    message.author === conversationsClient?.user.identity;

  useEffect(() => {
    if (isLast && participants && belongsToCurrentUser) {
      const getOtherParticipants = participants.filter(
        (p) => p.identity !== conversationsClient?.user.identity
      );
      setRead(
        Boolean(getOtherParticipants.length) &&
          getOtherParticipants.every(
            (p) => p.lastReadMessageIndex === message.index
          )
      );
    } else {
      setRead(false);
    }
  }, [
    participants,
    isLast,
    belongsToCurrentUser,
    conversationsClient,
    message,
  ]);

  useEffect(() => {
    if (focusable) {
      messageRef.current?.focus();
    }
  }, [focusable]);

  const renderMedia = () => {
    if (fileAttachmentConfig?.enabled) {
      if (!message.attachedMedia) {
        return null;
      }

      return message.attachedMedia.map((media: Media, index: Key) => {
        const file = {
          name: media.filename,
          type: media.contentType,
          size: media.size,
        } as File;
        return (
          <FilePreview
            key={index}
            file={file}
            isBubble={true}
            media={media}
            focusable={focusable}
          />
        );
      });
    }

    return <i>Media messages are not supported</i>;
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      const newFocusValue = message.index + (e.key === "ArrowUp" ? -1 : 1);
      updateFocus(newFocusValue);
    }
  };

  const handleMouseDown = () => {
    setIsMouseDown(true);
  };

  const handleMouseUp = () => {
    setIsMouseDown(false);
  };

  const handleFocus = () => {
    // Ignore focus from clicks
    if (!isMouseDown) {
      // Necessary since screen readers can set the focus to any focusable element
      updateFocus(message.index);
    }
  };

  const author =
    users?.find((u) => u.identity === message.author)?.friendlyName ||
    message.author;

  return (
    <Box
      {...outerContainerStyles}
      tabIndex={focusable ? 0 : -1}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      ref={messageRef}
      data-message-bubble
      data-testid="message-bubble"
    >
      <Box {...bubbleAndAvatarContainerStyles}>
        {(!belongsToCurrentUser || author == "Dentalpro") && (
          <img
            width="32px"
            src="https://storage-platform-prod.s3.amazonaws.com/logos/6785/logo.png?cb=15813865"
            style={{
              marginRight: "16px",
              marginBottom: "10px",
              alignSelf: "end",
            }}
          />
        )}
        <Box {...getInnerContainerStyles(belongsToCurrentUser)}>
          <Flex
            hAlignContent="between"
            width="100%"
            vAlignContent="center"
            marginBottom="space20"
          ></Flex>
          {message.body ? (
            parseMessageBody(
              message.body,
              belongsToCurrentUser,
              callChiediInformazioni,
              author
            )
          ) : (
            <></>
          )}
          {message.type === "media" ? renderMedia() : null}
        </Box>
      </Box>
    </Box>
  );
};
